import { useApolloClient } from '@apollo/react-hooks'
import {
  BiTubePriceCallMutation,
  BiTubePriceCallDocument,
  TubePriceCallMutation,
  TubePriceCallDocument,
} from '@src/mutations/ConfiguratorTubesMutation.generated'

import {
  useProductFromQueryParam,
  SingleTubeFormValues,
  checkForRedesignedBiTubeProduct,
  useProductInput,
} from './ProductsConfiguration'
import useFormikHelper from './useFormikHelper'
import { TubeProduct } from './enum'
import { TubeProperties } from '../../utils/enum'
import { getSessionDataFromFormikData } from './SessionDataUtil'
import { updateOligoDataWithPrice } from './utils'
import useTubeTotalValidation from './useTubeTotalValidation'

interface TubeTotalValidationPriceCallResponse {
  validateAndUpdatePrice: (e?: SingleTubeFormValues[]) => Promise<boolean>
}

const useTubeTotalValidationPriceCall =
  (): TubeTotalValidationPriceCallResponse => {
    const client = useApolloClient()
    const formik = useFormikHelper()
    const product = useProductFromQueryParam()
    const productDetails = useProductInput()
    const { totalValidationAPI } = useTubeTotalValidation()
    const updatedData = (oligoList: any[], validationData: any) => {
      if (
        !validationData ||
        !Array.isArray(validationData) ||
        validationData.length === 0
      ) {
        return oligoList
      }

      return oligoList.map((oligo, index) => {
        const matchingValidation = validationData.find(
          (v) => Number(v.oligoId) === index
        )
        return matchingValidation
          ? {
              ...oligo,
              listErrors: matchingValidation.listErrors || [],
            }
          : oligo
      })
    }

    const validateAndUpdatePrice = async (newList?: SingleTubeFormValues[]) => {
      const oligoListData = newList || formik.allValues.oligoList
      const computeFlag = (oligo) => {
        return product === TubeProduct.iscaledna &&
          ((formik.selectedOligoValues.purification === TubeProperties.VIVO &&
            oligo?.additionalServices?.length === 2) ||
            ((formik.selectedOligoValues.purification === TubeProperties.HPLC ||
              formik.selectedOligoValues.purification ===
                TubeProperties.DESALT) &&
              oligo?.additionalServices?.length === 1))
          ? 0
          : 1
      }
      const addFlagToOligos = (oligoList) => {
        return oligoList.map((oligo) => ({
          ...oligo,
          flag: computeFlag(oligo),
        }))
      }

      const oligosWithFlags = addFlagToOligos(oligoListData)

      const currentSessionData = getSessionDataFromFormikData(
        product,
        oligosWithFlags
      )

      try {
        const tubeTotalValidationData =
          await totalValidationAPI(currentSessionData)
        const validOligoIdList = tubeTotalValidationData
          ?.filter((data) => data?.listErrors?.length === 0)
          .map((data) => data.oligoId)

        const validList = oligoListData.filter((_, index) =>
          validOligoIdList?.includes(index)
        )

        const validOligosWithFlags = addFlagToOligos(validList)
        const sessionDataForPriceCall = getSessionDataFromFormikData(
          product,
          validOligosWithFlags
        )

        let updatedDataWithPrice: SingleTubeFormValues[] | [] = []
        if (sessionDataForPriceCall && sessionDataForPriceCall.length > 0) {
          const priceCallResponseData: any = checkForRedesignedBiTubeProduct(
            product
          )
            ? await client.mutate<BiTubePriceCallMutation>({
                mutation: BiTubePriceCallDocument,
                variables: { product: productDetails },
              })
            : await client.mutate<TubePriceCallMutation>({
                mutation: TubePriceCallDocument,
                variables: {
                  input: sessionDataForPriceCall,
                  productInput: productDetails,
                },
              })
          const priceResults: any =
            priceCallResponseData?.data?.tubePriceCall?.result ||
            priceCallResponseData?.data?.biTubePriceCall ||
            []

          updatedDataWithPrice = updateOligoDataWithPrice(
            validList,
            priceResults
          )
        } else {
          updatedDataWithPrice = validList
        }

        const allOligos = oligoListData.map((oligo, index) => {
          if (validOligoIdList?.includes(index)) {
            const validIndex = validOligoIdList.indexOf(index)
            return updatedDataWithPrice[validIndex]
          }
          return oligo
        })

        if (tubeTotalValidationData) {
          formik.setValues({
            ...formik.allValues,
            oligoList: updatedData(allOligos, tubeTotalValidationData),
          })
        }
        return true
      } catch (error) {
        console.error('Error in validation:', error)
      }
      return true
    }
    return {
      validateAndUpdatePrice: validateAndUpdatePrice,
    }
  }

export default useTubeTotalValidationPriceCall
